/**
 * VIP 视频上传前的 mp4 体检——**服务端与管端共用这一份**。
 *
 * 管端镜像是 `web/src/lib/mp4-inspect.ts`，**两份必须逐字节相同**（web/src/lib/upload.test.ts 比对）。
 * 不像 upload-contract 那样只镜像常量表：这里是一段解析逻辑，两边各写一遍迟早判得不一样——
 * 管端放行、服务端拒，运营白传 50MB；或者反过来，管端误拒一个本来能播的文件。
 * 所以本文件只许用 ES 标准库，不 import 任何东西，好让两边原样拷贝。
 *
 * 规则（方案《VIP视频改走公共上传服务》第五节，开发大佬 [580] 补边界）：
 *   1. 第一个 box 是 `ftyp`——不是 mp4 就拒
 *   2. `moov` 在 `mdat` 之前（faststart）——否则 APP 要把整个文件下完才能起播
 *   3. 不是分片 mp4：没有 `moof`，`moov` 里没有 `mvex`——分片文件 mvhd 的时长常是 0，APP 兼容性也差
 *   4. 视频轨全是 `avc1`（H.264）。同是 H.264 的 `avc3`（参数集放在码流里）也拒：ffmpeg 默认产出 avc1，
 *      页面给的转码命令不会出 avc3，少认一种比多认一种播放器兼容不了的稳（架构师 [591]）；有音轨时全是 `mp4a` 且 esds 标的是 AAC（mp4a 里也可能装 MP3）；
 *      `encv` / `enca`（加密）拒
 *   5. `mvhd` 读出的时长 > 0：向上取整到秒，最少 1 秒，随上传结果回给管端落库
 *
 * box 头的三种长度都认：普通 32 位、`size = 1` 的 64 位 largesize、`size = 0`「一直到文件末尾」。
 * mvhd 区分 version 0（时长 32 位）与 version 1（时长 64 位）。
 */

/** 拒收原因。管端拿它拼 i18n key（`upload.mp4.<reason>`），服务端在 upload-contract.ts 翻成中文 */
export type Mp4Reject =
  | "not_mp4" // 开头不是 ftyp
  | "moov_not_first" // mdat 在 moov 之前；或管端读的那一段里找不到 moov
  | "fragmented" // 分片 mp4（moof / mvex）
  | "encrypted" // encv / enca
  | "codec" // 视频不是 avc1，或音频不是 mp4a
  | "no_video" // 一条视频轨都没有
  | "duration" // mvhd 缺失、timescale 为 0、时长为 0 或「未知」
  | "corrupt"; // box 长度对不上，结构读不下去

export type Mp4Inspection = { ok: true; durationSec: number } | { ok: false; reason: Mp4Reject };

interface Box { type: string; start: number; body: number; end: number }

/** 解析失败往上抛这个，最外层统一收成 corrupt；业务拒收不走异常 */
class Corrupt extends Error {}

const fourcc = (b: Uint8Array, at: number): string => String.fromCharCode(b[at], b[at + 1], b[at + 2], b[at + 3]);

/**
 * 读 [from, limit) 里的一个 box 头。`limit` 是父 box 的边界（顶层就是已有字节的长度）。
 * `size = 0` 的 box 延伸到 limit；在顶层也就是「延伸到文件末尾」。
 */
function readBox(b: Uint8Array, v: DataView, from: number, limit: number): Box {
  if (from + 8 > limit) throw new Corrupt();
  const size32 = v.getUint32(from);
  const type = fourcc(b, from + 4);
  if (size32 === 1) {
    if (from + 16 > limit) throw new Corrupt();
    const size = Number(v.getBigUint64(from + 8));
    if (size < 16) throw new Corrupt();
    return { type, start: from, body: from + 16, end: from + size };
  }
  if (size32 === 0) return { type, start: from, body: from + 8, end: limit };
  if (size32 < 8) throw new Corrupt();
  return { type, start: from, body: from + 8, end: from + size32 };
}

/** 父 box 的直接子 box。子 box 越出父 box 视为损坏 */
function children(b: Uint8Array, v: DataView, from: number, end: number): Box[] {
  const out: Box[] = [];
  for (let at = from; at < end;) {
    const box = readBox(b, v, at, end);
    if (box.end > end) throw new Corrupt();
    out.push(box);
    at = box.end;
  }
  return out;
}

const child = (b: Uint8Array, v: DataView, parent: Box, type: string): Box | undefined =>
  children(b, v, parent.body, parent.end).find((x) => x.type === type);

/** mvhd → 秒（向上取整，最少 1）。读不出来、为 0、或全 1（「未知」）返回 null */
function durationOf(v: DataView, mvhd: Box): number | null {
  const version = v.getUint8(mvhd.body);
  let timescale: number;
  let duration: number;
  if (version === 1) {
    if (mvhd.body + 32 > mvhd.end) throw new Corrupt();
    timescale = v.getUint32(mvhd.body + 20);
    const d = v.getBigUint64(mvhd.body + 24);
    if (d === 0xffffffffffffffffn) return null;
    duration = Number(d);
  } else {
    if (mvhd.body + 20 > mvhd.end) throw new Corrupt();
    timescale = v.getUint32(mvhd.body + 12);
    duration = v.getUint32(mvhd.body + 16);
    if (duration === 0xffffffff) return null;
  }
  if (timescale === 0 || duration === 0) return null;
  return Math.max(1, Math.ceil(duration / timescale));
}

/** esds 里 MPEG-4 描述符的变长长度：每字节低 7 位，最高位 = 后面还有 */
function descLen(b: Uint8Array, at: number, end: number): [len: number, next: number] {
  let len = 0;
  for (let i = 0; i < 4; i++) {
    if (at >= end) throw new Corrupt();
    const x = b[at++];
    len = (len << 7) | (x & 0x7f);
    if (!(x & 0x80)) return [len, at];
  }
  return [len, at];
}

/**
 * `mp4a` 只说明「MPEG-4 音频容器」，里面可以是 AAC 也可以是 MP3。
 * 真正的编码在 esds → DecoderConfigDescriptor 的 objectTypeIndication：
 * 0x40 = MPEG-4 Audio（AAC），0x66~0x68 = MPEG-2 AAC。其余（0x69 / 0x6B 是 MP3）都不算 AAC。
 */
function isAacEntry(b: Uint8Array, v: DataView, entry: Box): boolean {
  // AudioSampleEntry：SampleEntry(8) + 版本(2) 起的固定 28 字节；QuickTime 声音描述 v1 / v2 再多 16 / 36 字节
  if (entry.body + 28 > entry.end) throw new Corrupt();
  const qtVersion = v.getUint16(entry.body + 8);
  const from = entry.body + 28 + (qtVersion === 1 ? 16 : qtVersion === 2 ? 36 : 0);
  const esds = children(b, v, from, entry.end).find((x) => x.type === "esds");
  if (!esds) return false;
  let at = esds.body + 4; // version/flags
  if (b[at++] !== 0x03) return false; // ES_Descriptor
  [, at] = descLen(b, at, esds.end);
  at += 2; // ES_ID
  const flags = b[at++];
  if (flags & 0x80) at += 2; // dependsOn_ES_ID
  if (flags & 0x40) at += 1 + b[at]; // URL
  if (flags & 0x20) at += 2; // OCR_ES_Id
  if (at >= esds.end || b[at++] !== 0x04) return false; // DecoderConfigDescriptor
  [, at] = descLen(b, at, esds.end);
  if (at >= esds.end) throw new Corrupt();
  const oti = b[at];
  return oti === 0x40 || (oti >= 0x66 && oti <= 0x68);
}

/** 一条轨：handler（vide / soun / …）与它 stsd 里每个 sample entry 的类型；音频另记是不是 AAC */
function trackOf(b: Uint8Array, v: DataView, trak: Box): { handler: string; entries: string[]; aac: boolean } | null {
  const mdia = child(b, v, trak, "mdia");
  if (!mdia) return null;
  const hdlr = child(b, v, mdia, "hdlr");
  if (!hdlr || hdlr.body + 12 > hdlr.end) throw new Corrupt();
  // hdlr：version/flags(4) + pre_defined(4) + handler_type(4)
  const handler = fourcc(b, hdlr.body + 8);
  const stbl = (() => {
    const minf = child(b, v, mdia, "minf");
    return minf && child(b, v, minf, "stbl");
  })();
  const stsd = stbl && child(b, v, stbl, "stsd");
  if (!stsd) return { handler, entries: [], aac: false };
  // stsd：version/flags(4) + entry_count(4)，后面紧跟 entry_count 个 sample entry box
  if (stsd.body + 8 > stsd.end) throw new Corrupt();
  const boxes = children(b, v, stsd.body + 8, stsd.end);
  const aac = handler === "soun" && boxes.every((x) => x.type === "mp4a" && isAacEntry(b, v, x));
  return { handler, entries: boxes.map((x) => x.type), aac };
}

function inspectMoov(b: Uint8Array, v: DataView, moov: Box): Mp4Inspection {
  const kids = children(b, v, moov.body, moov.end);
  if (kids.some((x) => x.type === "mvex")) return { ok: false, reason: "fragmented" };
  let videoTracks = 0;
  for (const trak of kids.filter((x) => x.type === "trak")) {
    const t = trackOf(b, v, trak);
    if (!t) continue;
    if (t.entries.includes("encv") || t.entries.includes("enca")) return { ok: false, reason: "encrypted" };
    if (t.handler === "vide") {
      if (t.entries.length === 0 || t.entries.some((e) => e !== "avc1")) return { ok: false, reason: "codec" };
      videoTracks++;
    } else if (t.handler === "soun") {
      if (t.entries.length === 0 || !t.aac) return { ok: false, reason: "codec" };
    }
    // 其余轨（时间码 tmcd、字幕、元数据）不影响播放，不管
  }
  if (videoTracks === 0) return { ok: false, reason: "no_video" };
  const mvhd = kids.find((x) => x.type === "mvhd");
  const sec = mvhd ? durationOf(v, mvhd) : null;
  return sec == null ? { ok: false, reason: "duration" } : { ok: true, durationSec: sec };
}

/**
 * @param bytes 文件字节。服务端传整个文件；管端只传开头一段（见 MP4_PREFIX_BYTES）
 * @param complete bytes 是不是整个文件。
 *   - true（服务端）：从头扫到尾，顶层任何地方出现 moof 都算分片
 *   - false（管端）：在这段里找不到完整的 moov 就判 moov_not_first——faststart 的文件
 *     moov 紧跟 ftyp，一般几百 KB，读开头几 MB 还找不到，基本就是索引在末尾。
 *     它只是让运营少白传一趟，服务端还会拿整个文件再判一次
 */
export function inspectMp4(bytes: Uint8Array, complete: boolean): Mp4Inspection {
  const v = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const limit = bytes.byteLength;
  try {
    if (limit < 8 || fourcc(bytes, 4) !== "ftyp") return { ok: false, reason: "not_mp4" };
    let moov: Box | null = null;
    for (let at = 0; at < limit;) {
      // 管端那一段的切口正好落在某个顶层 box 的头里（8 字节，largesize 是 16）：头都读不全，
      // 按「这段读到头了」处理，不判损坏——后面没找到 moov 自然落到 moov_not_first（架构师 [591]）
      if (!complete && (at + 8 > limit || (v.getUint32(at) === 1 && at + 16 > limit))) break;
      const box = readBox(bytes, v, at, limit);
      if (box.type === "moof") return { ok: false, reason: "fragmented" };
      if (box.type === "mdat" && !moov) return { ok: false, reason: "moov_not_first" };
      if (box.type === "moov") {
        if (box.end > limit) {
          if (complete) throw new Corrupt();
          return { ok: false, reason: "moov_not_first" };
        }
        moov = box;
      }
      if (box.end > limit) {
        // 管端那一段读到这里就断了：moov 已经找到就够了，没找到按「索引不在开头」
        if (complete) throw new Corrupt();
        break;
      }
      at = box.end;
    }
    if (!moov) return { ok: false, reason: complete ? "corrupt" : "moov_not_first" };
    return inspectMoov(bytes, v, moov);
  } catch (e) {
    if (e instanceof Corrupt || e instanceof RangeError) return { ok: false, reason: "corrupt" };
    throw e;
  }
}

/**
 * 管端读开头多少字节做本地体检。faststart 的 moov 一般几百 KB（2 分钟 1080p 约 100~300KB），
 * 8MB 留足余量；也不至于让浏览器为一次本地检查读太多。
 */
export const MP4_PREFIX_BYTES = 8 * 1024 * 1024;
